import { supabase } from '@/integrations/supabase/client';

interface FormTriggerValidation {
  canProceed: boolean;
  missingForms: Array<{
    id: string;
    name: string;
    isRequired: boolean;
  }>;
  message?: string;
}

export const validateFormRequirements = async (
  jobId: string,
  currentStageId: string,
  nextStageId: string,
  orgId: string
): Promise<FormTriggerValidation> => {
  try {
    // Get all form triggers for the current stage (exit triggers)
    const { data: exitTriggers, error: exitError } = await supabase
      .from('form_triggers')
      .select(`
        *,
        form_template:form_templates(*)
      `)
      .eq('stage_id', currentStageId)
      .eq('trigger_on', 'exit_stage')
      .eq('org_id', orgId);

    if (exitError) throw exitError;

    // Get all form triggers for the next stage (enter triggers)
    const { data: enterTriggers, error: enterError } = await supabase
      .from('form_triggers')
      .select(`
        *,
        form_template:form_templates(*)
      `)
      .eq('stage_id', nextStageId)
      .eq('trigger_on', 'enter_stage')
      .eq('org_id', orgId);

    if (enterError) throw enterError;

    const allTriggers = [...(exitTriggers || []), ...(enterTriggers || [])];
    const requiredTriggers = allTriggers.filter(t => t.is_required);

    if (requiredTriggers.length === 0) {
      return { canProceed: true, missingForms: [] };
    }

    // Check which required forms are completed
    const { data: submissions, error: subError } = await supabase
      .from('form_submissions')
      .select('form_template_id, status')
      .eq('job_id', jobId)
      .in('status', ['submitted', 'approved']);

    if (subError) throw subError;

    const completedFormIds = new Set(
      submissions?.map(s => s.form_template_id) || []
    );

    const missingForms = requiredTriggers
      .filter(trigger => !completedFormIds.has(trigger.form_template_id))
      .map(trigger => ({
        id: trigger.form_template_id,
        name: trigger.form_template?.name || 'Unknown Form',
        isRequired: trigger.is_required,
      }));

    if (missingForms.length > 0) {
      return {
        canProceed: false,
        missingForms,
        message: `Please complete the following required forms before proceeding: ${missingForms.map(f => f.name).join(', ')}`,
      };
    }

    return { canProceed: true, missingForms: [] };
  } catch (error) {
    console.error('Error validating form requirements:', error);
    return {
      canProceed: true, // Fail open to not block workflow
      missingForms: [],
      message: 'Could not validate form requirements',
    };
  }
};

export const triggerFormAttachments = async (
  jobId: string,
  stageId: string,
  triggerType: 'enter_stage' | 'exit_stage',
  orgId: string
) => {
  try {
    const { data: triggers, error } = await supabase
      .from('form_triggers')
      .select('*, form_template:form_templates(*)')
      .eq('stage_id', stageId)
      .eq('trigger_on', triggerType)
      .eq('org_id', orgId);

    if (error) throw error;

    // Auto-create draft submissions for triggered forms that don't exist yet
    for (const trigger of triggers || []) {
      const { data: existing } = await supabase
        .from('form_submissions')
        .select('id')
        .eq('job_id', jobId)
        .eq('form_template_id', trigger.form_template_id)
        .single();

      if (!existing) {
        await supabase.from('form_submissions').insert({
          job_id: jobId,
          form_template_id: trigger.form_template_id,
          org_id: orgId,
          status: 'draft',
          submitted_data: {},
        });
      }
    }
  } catch (error) {
    console.error('Error triggering form attachments:', error);
  }
};
